/**
 * Utility functions for formatting data display
 */

/**
 * Get user's browser timezone
 * Uses Intl API to detect the user's local timezone automatically
 */
const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Fallback to Europe/Berlin if detection fails
    return 'Europe/Berlin';
  }
};

/**
 * Parse date string or timestamp and ensure it's treated as UTC
 * Backend sends timestamps without 'Z' suffix, so we need to handle this
 * Also handles numeric timestamps (milliseconds since epoch)
 */
const parseAsUTC = (dateInput: string | number): Date => {
  // Handle numeric timestamps
  if (typeof dateInput === 'number') {
    return new Date(dateInput);
  }

  // If string doesn't end with 'Z', append it to mark as UTC
  const utcString = dateInput.endsWith('Z') ? dateInput : `${dateInput}Z`;
  return new Date(utcString);
};

/**
 * Format bytes to human-readable MB format
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) {return '0 MB';}
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

/**
 * Format date to localized string with user's timezone
 */
export const formatDate = (dateInput: string | number | null | undefined, locale: string = 'en-US'): string => {
  if (!dateInput) {return '-';}

  const timeZone = getUserTimezone();
  const date = parseAsUTC(dateInput);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone
  }).format(date);
};

/**
 * Format date with time to localized string with user's timezone
 */
export const formatDateTime = (dateInput: string | number | null | undefined, locale: string = 'en-US'): string => {
  if (!dateInput) {return '-';}

  const timeZone = getUserTimezone();
  const date = parseAsUTC(dateInput);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone
  }).format(date);
};

/**
 * Format time to localized string with user's timezone
 */
export const formatTime = (
  dateInput: string | number | null | undefined,
  locale: string = 'en-US'
): string => {
  if (!dateInput) {return '-';}

  const timeZone = getUserTimezone();
  const date = parseAsUTC(dateInput);
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone
  }).format(date);
};

/**
 * Format subscription tier for display
 */
export const formatSubscriptionTier = (tier?: string): string => {
  return tier?.toUpperCase() || 'FREE';
};

/**
 * User display information interface
 * Represents the owner/user information that might be available
 */
export interface UserDisplayInfo {
  owner_full_name?: string | null;
  owner_username?: string | null;
  owner_email?: string | null;
  owner_id?: string | null;
}

/**
 * Get the best available display name for a user
 * Priority: username > full_name > email > user_id
 *
 * @param user - User object with optional display fields
 * @param fallback - Fallback text if no information is available (default: 'Unknown User')
 * @returns The best available display name for the user
 *
 * @example
 * getUserDisplayName({ owner_username: 'mmustermann' }) // Returns: 'mmustermann'
 * getUserDisplayName({ owner_full_name: 'Max Mustermann' }) // Returns: 'Max Mustermann'
 * getUserDisplayName({ owner_id: '123-456' }) // Returns: '123-456'
 * getUserDisplayName({}) // Returns: 'Unknown User'
 */
export const getUserDisplayName = (
  user: UserDisplayInfo,
  fallback: string = 'Unknown User'
): string => {
  // Priority 1: Username (most consistent)
  if (user.owner_username?.trim()) {
    return user.owner_username.trim();
  }

  // Priority 2: Full name
  if (user.owner_full_name?.trim()) {
    return user.owner_full_name.trim();
  }

  // Priority 3: Email address
  if (user.owner_email?.trim()) {
    return user.owner_email.trim();
  }

  // Priority 4: User ID (UUID fallback)
  if (user.owner_id?.trim()) {
    return user.owner_id.trim();
  }

  // Final fallback
  return fallback;
};